// covered-by: frontends/carrie/src/ask-v2/routes/__tests__/authLayout.test.tsx
// eslint-disable-next-line import/named
import { createRoute, redirect, useRouter } from "@tanstack/react-router";

import PendingComponent from "../../components/PendingComponent";
import { SIDEBAR_STORAGE_NAME } from "../../components/sidebar/SidebarProvider";
import { requireFlagsLoaded } from "../../routes/auth-layout/requireFlagsLoaded";
import { loginRoute } from "../../routes/login/route";
import { rootRoute } from "../../routes/root/route";
import { getGuidelines } from "../../services/brand-guidelines";
import { getEnterprises } from "../../services/enterprises";
import { useAppSelector } from "../../utils/hooks";
import { logoutUrl } from "../../utils/logoutUrl";
import { queryClient } from "../../utils/queryClient";
import { setDrawerOpen } from "../../utils/slices/ui";
import { store } from "../../utils/store";
import { AskV2Layout } from "../shell/AskV2Layout";

// why: reading storage throws SecurityError rather than returning null when it is
// blocked (private mode, third-party-cookie blocking, sandboxed iframe). beforeLoad
// runs on every route resolution, so an unguarded read white-screens the route
// instead of falling back to the default drawer preference. Mirrors the branch route.
function readLocalStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Pathless authenticated layout for Ask V2. Reuses login, not PunttLayout —
 * the tickets sidebar must not appear on ask.puntt.ai.
 */
export const askV2AuthLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "ask-v2-authenticated",
  async beforeLoad({ location }) {
    const { value: user } = store.getState().auth;
    if (user == null) {
      throw redirect({
        to: loginRoute.to,
        search: {
          redirect: location.pathname,
        },
      });
    }

    // Prime the PostHog flags AND the two query caches that child `beforeLoad`
    // guards read SYNCHRONOUSLY, before any of those guards run.
    //
    // why: TanStack runs every matched route's `beforeLoad` top-down before any
    // `loader`, so a `loader`-level prime is too late. `settingsRoutes.ts` hangs
    // `canAccessAskPuntt()` / `isMeaningfulGigsOrDemo()` / `canAccessKnowledgeDatabase()`
    // off this layout, and those read `queryClient.getQueryData(["brand-guidelines"])`
    // and `(["enterprises"])` with no await — a cache miss reads as `false`, not
    // "unknown". Without this prime a hard refresh or deep link into
    // ask.puntt.ai/roi, /notifications-settings or /settings/knowledge-database
    // was bounced straight back to the V2 home by `denyToAskHome()`, and
    // `AskV2Sidebar` (same sync reads, non-reactive) hid the matching Settings
    // entries for the rest of the page's life. This is the same miss the sibling
    // layout (routes/auth-layout/route.tsx) already primes against.
    //
    // flagVariant is undefined until PostHog's callback, and the KB guard treats
    // that as deny-to-home. Every entry is swallowed like the sibling layout's:
    // a throwing PostHog subscriber or a hiccup on /private/catalyst/enterprises
    // or /brand-guidelines must degrade to "read off", not take down the whole
    // authenticated shell.
    await Promise.all([
      requireFlagsLoaded().catch(() => undefined),
      queryClient
        .ensureQueryData({
          queryKey: ["enterprises"],
          queryFn: getEnterprises,
        })
        .catch(() => undefined),
      queryClient
        .ensureQueryData({
          queryKey: ["brand-guidelines"],
          queryFn: getGuidelines,
        })
        .catch(() => undefined),
    ]);

    store.dispatch(
      setDrawerOpen({
        app:
          (readLocalStorage(`app_${SIDEBAR_STORAGE_NAME}`) ?? "true") ===
          "true",
        comments:
          (readLocalStorage(`comments_${SIDEBAR_STORAGE_NAME}`) ?? "true") ===
          "true",
      }),
    );
  },
  component: AskV2AuthLayout,
  pendingComponent: PendingComponent,
});

export function AskV2AuthLayout() {
  const router = useRouter();
  const user = useAppSelector((state) => state.auth.value);

  // why: beforeLoad only runs on navigation — Account → Log out clears Redux
  // without rematching routes, so the layout must hard-redirect like classic AuthLayout
  if (user == null && router.state.location.pathname !== loginRoute.to) {
    location.assign(logoutUrl(router.basepath));
    return null;
  }

  return <AskV2Layout />;
}
